/**
 * F1.6.0 Contract & Spec Review for a job. CR-2026-CONTRACT-REVIEW (2026-10-15), TR-CONTRACT-REVIEW-001 Rev 0.
 * Route: /jobs/:id/contract-review   Server: /api/contract-review (sfm-web-backend src/routes/contractReview.js)
 *
 * Tabs: Management view | Questions | Documents | Sign-off | History.
 * Gate CR-V01: a job created from go-live does no release, package activation, material issue or station work until its
 * current revision is ACCEPTED by every required signatory (or a CONTRACT_REVIEW override, Admin + QC co-sign, is live).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Alert, Box, Button, Card, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Radio,
  RadioGroup, Snackbar, Stack, Switch, Tab, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from "@mui/material";
import ApiCall from "../../utils/apicall";
import ManagementView from "./ManagementView";
import QuestionsPanel, { openSource } from "./QuestionsPanel";
import { REOPENING_ROLES, withdrawEffect, STATUS_COLOR, STATUS_LABEL, SOURCE_ROLE_LABEL, segregationProblem, signProblem, signableSlots, sourceProblem } from "./contractReviewModel";

import SmartTable from "../../components/SmartTable/SmartTable";
const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;

function SignDialog({ open, slot, review, me, onClose, onSaved }) {
  // Review P2-3: nothing is pre-selected, and each decision sent to this line is shown and answered here.
  const [decision, setDecision] = useState("");
  const [comment, setComment] = useState("");
  const [outcomes, setOutcomes] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { setDecision(""); setComment(""); setOutcomes({}); setErr(null); }, [slot]);
  if (!slot) return null;
  const items = slot.decisionItems || [];
  const save = async () => {
    const sod = segregationProblem(review, slot.key, me.userId);
    if (sod) { setErr(sod); return; }
    const p = signProblem(decision, comment, items, outcomes);
    if (p) { setErr(p); return; }
    setBusy(true); setErr(null);
    try {
      const decisions = {};
      for (const it of items) { const o = String(outcomes[it.key] || "").trim(); if (o) decisions[it.key] = o; }
      const r = await ApiCall.post(`/contract-review/reviews/${review.id}/sign`, { slot: slot.key, decision, comment: comment.trim() || undefined, decisions });
      onSaved(r.data);
    } catch (e) { setErr(msgOf(e, "Could not sign.")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sign F1.6.0 revision {review.revision} as {slot.label}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 1.5 }}>Your signature is final for this revision. Hold or Reject stops acceptance; the PM then revises the review.</Alert>
        {items.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{items.length} decision{items.length === 1 ? " is" : "s are"} sent to you. Record each one; it prints on the F1.6.0 record.</Typography>
            {items.map((it) => (
              <Box key={it.key} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.question}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Asked: {it.note}</Typography>
                <TextField fullWidth multiline minRows={2} size="small" label={decision === "HOLD" || decision === "REJECT" ? "Your decision (if made)" : "Your decision (required to accept)"}
                  value={outcomes[it.key] || ""} onChange={(e) => setOutcomes((o) => ({ ...o, [it.key]: e.target.value }))} />
              </Box>
            ))}
          </Box>
        )}
        <RadioGroup row value={decision} onChange={(e) => setDecision(e.target.value)}>
          <FormControlLabel value="ACCEPT" control={<Radio />} label="Accept" />
          <FormControlLabel value="HOLD" control={<Radio />} label="Hold" />
          <FormControlLabel value="REJECT" control={<Radio />} label="Reject" />
        </RadioGroup>
        <TextField fullWidth multiline minRows={2} size="small" label={decision === "HOLD" || decision === "REJECT" ? "Reason (required)" : "Comment (optional)"}
          value={comment} onChange={(e) => setComment(e.target.value)} sx={{ mt: 1 }} />
        {err && <Alert severity="error" sx={{ mt: 1.5 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={busy || !decision} onClick={save}>Sign</Button></DialogActions>
    </Dialog>
  );
}

export default function ContractReview() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState(0);
  const [msg, setMsg] = useState(null);
  const [file, setFile] = useState(null);
  const [role, setRole] = useState("CONTRACT");
  const [busy, setBusy] = useState(false);
  const [signSlot, setSignSlot] = useState(null);
  const [reasonDlg, setReasonDlg] = useState(null); // { kind: 'withdraw'|'revise'|'slot'|'source', slot?, source? }
  const [reason, setReason] = useState("");
  const [oldRev, setOldRev] = useState(null);

  const load = useCallback(async () => {
    try { const r = await ApiCall.get(`/contract-review/jobs/${id}`); setData(r.data.data); setErr(null); }
    catch (e) { setErr(msgOf(e, "Could not load the contract review.")); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const setReview = (rev) => setData((d) => ({ ...d, review: rev }));
  const refresh = async (m) => { if (m) setMsg(m); await load(); };

  const upload = async () => {
    const p = sourceProblem(file, role, data?.catalog?.roles);
    if (p) { setMsg({ severity: "error", text: p }); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("role", role);
      const r = await ApiCall.post(`/contract-review/jobs/${id}/sources`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setFile(null); await refresh({ severity: "success", text: r.data.message });
    } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not add the document.") }); } finally { setBusy(false); }
  };
  const post = async (path, body, okText) => {
    setBusy(true);
    try { const r = await ApiCall.post(path, body || {}); await refresh({ severity: "success", text: r.data.message || okText }); return true; }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not save."), problems: e?.response?.data?.problems }); return false; }
    finally { setBusy(false); }
  };
  const printPdf = async (reviewId) => {
    try {
      const r = await ApiCall.get(`/contract-review/reviews/${reviewId}/pdf`, { responseType: "blob", timeout: 120000 });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank", "noopener");
    } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not open the F1.6.0 record.") }); }
  };
  const setSlotUnused = async (slot, unused, why) => {
    try { await ApiCall.put(`/contract-review/slots/${slot.key}`, { unused, reason: why ? why.trim() : undefined }); await refresh({ severity: "success", text: "Saved." }); return true; }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not save.") }); return false; }
  };

  if (err) return <Container><Alert severity="error" sx={{ mt: 2 }}>{err}</Alert></Container>;
  if (!data) return <Container><Typography sx={{ mt: 2 }}>Loading...</Typography></Container>;
  const r = data.review;
  const me = data.me;
  const mine = signableSlots(r, me.slots);
  return (
    <>
      <Helmet><title>F1.6.0 Contract Review | SFM</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1.5} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4">F1.6.0 Contract &amp; Spec Review</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{data.job.name}</Typography>
              {r && <Chip size="small" label={`Rev ${r.revision}: ${STATUS_LABEL[r.status]}`} color={STATUS_COLOR[r.status]} />}
              {!data.job.gated ? <Chip size="small" variant="outlined" label="Existed at go-live: not gated" />
                : data.gate.ok ? <Chip size="small" color="success" label={data.gate.overridden ? "Release open (override live)" : "Release open"} />
                  : <Chip size="small" color="error" label="Release blocked (CR-V01)" />}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            {r && <Button variant="outlined" onClick={() => printPdf(r.id)}>{r.record ? "F1.6.0 record (PDF)" : "F1.6.0 working copy (PDF)"}</Button>}
            <Button variant="outlined" component={RouterLink} to={`/jobs/${id}/detailing`}>Job</Button>
          </Stack>
        </Stack>
        {r && r.record && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Revision {r.revision} was {String(r.record.status).toLowerCase()} and its F1.6.0 record was frozen {new Date(r.record.frozenAt).toLocaleString()} (SHA-256 {r.record.sha256.slice(0, 16)}...).
            The sign-off lines, RFI states and documents shown are as they were at that decision.
          </Alert>
        )}
        {data.overrides && data.overrides.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Contract review override {data.overrides.map((o) => `#${o.id} (${o.status.toLowerCase()}${o.expires_at ? `, until ${new Date(o.expires_at).toLocaleString()}` : ""})`).join(", ")} is on the Daily Red Report.
          </Alert>
        )}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }} variant="scrollable">
          <Tab label="Management view" /><Tab label="Questions" disabled={!r} /><Tab label={`Documents (${data.sources.length})`} />
          <Tab label={mine.length ? `Sign-off (${mine.length} for you)` : "Sign-off"} disabled={!r} /><Tab label="History" />
        </Tabs>

        {tab === 0 && <ManagementView data={data} />}

        {tab === 1 && r && (
          <>
            {r.status === "DRAFT" && me.canEdit && (
              <Card sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
                  <TextField select size="small" label="Fabrication and erectability" value={r.complexity || ""} sx={{ minWidth: 240 }}
                    onChange={async (e) => {
                      try { const x = await ApiCall.patch(`/contract-review/reviews/${r.id}`, { complexity: e.target.value, rowVersion: r.rowVersion }); setReview(x.data.data); }
                      catch (er) { setMsg({ severity: "error", text: msgOf(er, "Could not save.") }); }
                    }}>
                    {["SIMPLE", "CONVENTIONAL", "COMPLEX"].map((c) => <MenuItem key={c} value={c}>{c[0] + c.slice(1).toLowerCase()}</MenuItem>)}
                  </TextField>
                  <Button variant="outlined" disabled={busy} onClick={() => post(`/contract-review/reviews/${r.id}/capture`)}>Run capture again</Button>
                  <Box sx={{ flex: 1 }} />
                  <Button variant="contained" disabled={busy} onClick={() => post(`/contract-review/reviews/${r.id}/submit`)}>Submit for sign-off</Button>
                </Stack>
                {r.submitProblems.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    Before submitting: {r.submitProblems.join(" ")}
                  </Alert>
                )}
              </Card>
            )}
            {r.status === "SUBMITTED" && me.canEdit && (
              <Alert severity="info" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => { setReason(""); setReasonDlg({ kind: "withdraw" }); }}>Withdraw</Button>}>
                Submitted for sign-off: the answers are locked. Withdraw to correct them (a new revision is started; signatures so far stay on record).
              </Alert>
            )}
            {["ACCEPTED", "HELD", "REJECTED"].includes(r.status) && me.canEdit && (
              <Alert severity={r.status === "ACCEPTED" ? "success" : "warning"} sx={{ mb: 2 }}
                action={<Button color="inherit" onClick={() => { setReason(""); setReasonDlg({ kind: "revise" }); }}>Start revision {r.revision + 1}</Button>}>
                Revision {r.revision} is {STATUS_LABEL[r.status].toLowerCase()}. {r.status === "ACCEPTED" ? "A new revision stops new release, package activation and material issue (heat links included) until it is accepted; stage and station work on pieces already released continue." : "Revise it to answer the signatory."}
              </Alert>
            )}
            <QuestionsPanel review={r} canEdit={me.canEdit} onChange={setReview} onMessage={setMsg} />
          </>
        )}

        {tab === 2 && (
          <Card sx={{ p: 2 }}>
            {me.canEdit && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
                <TextField select size="small" label="Document" value={role} onChange={(e) => setRole(e.target.value)} sx={{ minWidth: 200 }}>
                  {(data.catalog.roles || []).map((x) => <MenuItem key={x} value={x}>{SOURCE_ROLE_LABEL[x]}</MenuItem>)}
                </TextField>
                <Button variant="outlined" component="label">{file ? file.name : "Choose PDF"}
                  <input hidden type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} />
                </Button>
                <Button variant="contained" disabled={busy || !file} onClick={upload}>{busy ? "Reading every page..." : "Add and capture"}</Button>
              </Stack>
            )}
            {r && ["SUBMITTED", "ACCEPTED", "HELD", "REJECTED"].includes(r.status) && me.canEdit && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {REOPENING_ROLES.includes(role)
                  ? `Adding a ${SOURCE_ROLE_LABEL[role].toLowerCase()} now starts a new revision.${r.status === "ACCEPTED" ? " New release, package activation and material issue (heat links included) wait until it is accepted again; stage and station work on pieces already released continue." : ""}`
                  : `A ${SOURCE_ROLE_LABEL[role].toLowerCase()} is filed with the review and read at the next revision. It does not reopen revision ${r.revision}.`}
              </Alert>
            )}
            <SmartTable tableId="contract-documents" lockedColumns={["Document", "SHA-256"]} size="small">
              <TableHead><TableRow><TableCell>Document</TableCell><TableCell>Type</TableCell><TableCell>Pages</TableCell><TableCell>Added</TableCell><TableCell>SHA-256</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {data.sources.length === 0 && <TableRow><TableCell colSpan={6} sx={{ color: "text.secondary" }}>No documents yet. Add the contract or PO and the specifications; capture runs on every page.</TableCell></TableRow>}
                {data.sources.map((s) => (
                  <TableRow key={s.id} hover sx={{ cursor: "pointer" }} onClick={() => openSource(s.id).catch((e) => setMsg({ severity: "error", text: msgOf(e, "Could not open.") }))}>
                    <TableCell sx={s.withdrawn ? { color: "text.secondary" } : undefined}>
                      {s.name}
                      {s.withdrawn && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Withdrawn {new Date(s.withdrawn.at).toLocaleDateString()} by {s.withdrawn.byName}: {s.withdrawn.reason}. Capture no longer reads it.
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{SOURCE_ROLE_LABEL[s.role]}{s.withdrawn && <Chip size="small" sx={{ ml: 1 }} label="Withdrawn" />}</TableCell>
                    <TableCell>{s.pages}{s.noTextLayer && <Chip size="small" color="warning" sx={{ ml: 1 }} label={`${s.pages - s.pagesWithText} scanned: manual entry`} />}</TableCell>
                    <TableCell>{s.uploadedByName}, {new Date(s.uploadedAt).toLocaleString()} (rev {s.addedInRevision})</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{s.sha256.slice(0, 16)}...</TableCell>
                    <TableCell align="right">
                      {me.canEdit && !s.withdrawn && (
                        <Button size="small" onClick={(e) => { e.stopPropagation(); setReason(""); setReasonDlg({ kind: "source", source: s }); }}>Withdraw</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </SmartTable>
          </Card>
        )}

        {tab === 3 && r && (
          <Card sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Each line is signed by a person holding its role. The QC Manager and Project Manager lines are signed by different people.
            </Typography>
            <SmartTable tableId="contract-signatures" lockedColumns={["Line", "Role", "Signature"]} size="small">
              <TableHead><TableRow><TableCell>Line</TableCell><TableCell>Role</TableCell><TableCell>Signature</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {r.slots.map((s) => (
                  <TableRow key={s.key}>
                    <TableCell sx={{ fontWeight: 600 }}>{s.label}</TableCell>
                    <TableCell>{s.role}</TableCell>
                    <TableCell>
                      {s.unused ? `Not used at this company (${s.unusedReason})` : s.signature
                        ? `${s.signature.decision} by ${s.signature.name}, ${new Date(s.signature.signedAt).toLocaleString()}${s.signature.comment ? `: "${s.signature.comment}"` : ""}`
                        : "Not signed"}
                    </TableCell>
                    <TableCell align="right">
                      {mine.includes(s.key) && <Button size="small" variant="contained" onClick={() => setSignSlot(s)}>Sign</Button>}
                      {me.canConfigure && !s.required && !s.signature && (
                        <FormControlLabel label="Not used" control={<Switch size="small" checked={s.unused} onChange={(e) => (e.target.checked ? (setReason(""), setReasonDlg({ kind: "slot", slot: s })) : setSlotUnused(s, false))} />} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </SmartTable>
          </Card>
        )}

        {tab === 4 && (
          <Card sx={{ p: 2 }}>
            <SmartTable tableId="contract-revisions" lockedColumns={["Rev", "Status"]} size="small">
              <TableHead><TableRow><TableCell>Rev</TableCell><TableCell>Status</TableCell><TableCell>Started</TableCell><TableCell>Why</TableCell><TableCell>Decided</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {data.revisions.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>{v.revision}</TableCell>
                    <TableCell><Chip size="small" label={STATUS_LABEL[v.status]} color={STATUS_COLOR[v.status]} /></TableCell>
                    <TableCell>{v.createdByName}, {new Date(v.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{v.createdReason}{v.supersededReason ? ` / superseded: ${v.supersededReason}` : ""}</TableCell>
                    <TableCell>{v.decidedAt ? new Date(v.decidedAt).toLocaleString() : ""}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => printPdf(v.id)}>PDF</Button>
                      <Button size="small" onClick={async () => { try { const x = await ApiCall.get(`/contract-review/reviews/${v.id}`); setOldRev(x.data.data); } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not load.") }); } }}>View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </SmartTable>
            {oldRev && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Revision {oldRev.revision} ({STATUS_LABEL[oldRev.status]}), read only</Typography>
                <ManagementView data={{ ...data, review: oldRev }} />
              </Box>
            )}
          </Card>
        )}
      </Container>

      <SignDialog open={!!signSlot} slot={signSlot} review={r} me={me} onClose={() => setSignSlot(null)}
        onSaved={async (d) => { setSignSlot(null); await refresh({ severity: "success", text: d.message }); }} />
      <Dialog open={!!reasonDlg} onClose={() => setReasonDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{reasonDlg?.kind === "withdraw" ? "Withdraw the submission" : reasonDlg?.kind === "slot" ? `${reasonDlg.slot.label}: not used at this company`
          : reasonDlg?.kind === "source" ? `Withdraw ${reasonDlg.source.name}` : "Start a new revision"}</DialogTitle>
        <DialogContent>
          {reasonDlg?.kind === "source" && (
            <Alert severity="info" sx={{ mt: 1 }}>{withdrawEffect(reasonDlg.source.role, r && r.status)}</Alert>
          )}
          <TextField fullWidth multiline minRows={2} size="small" label="Reason (stays on the record)" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDlg(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || reason.trim().length < (reasonDlg?.kind === "source" ? 10 : 4)}
            onClick={async () => {
              const ok = reasonDlg.kind === "slot" ? await setSlotUnused(reasonDlg.slot, true, reason)
                : reasonDlg.kind === "source" ? await post(`/contract-review/sources/${reasonDlg.source.id}/withdraw`, { reason: reason.trim() })
                  : await post(`/contract-review/reviews/${r.id}/${reasonDlg.kind}`, { reason: reason.trim() });
              if (ok) setReasonDlg(null);
            }}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!msg} autoHideDuration={msg?.severity === "error" ? 12000 : 5000} onClose={() => setMsg(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {msg ? <Alert severity={msg.severity} onClose={() => setMsg(null)} sx={{ maxWidth: 720 }}>{msg.text}</Alert> : <span />}
      </Snackbar>
    </>
  );
}

